/* oxlint-disable no-new */
import { arrayToObject } from 'lib/array/arrayUtils'
import config from 'constants/config'
import switchFunc from 'lib/function/switchFunc'
import { DEFAULT_LOCALE, getLocaleWithWesternDigits, isEnglishLocale, resolveActiveAppLocale } from 'i18n/util/locale'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const dollarPrefixes: Record<string, string> = {
  AUD: 'A',
  CAD: 'CA',
  HKD: 'HK',
  MOP: 'MOP',
  NZD: 'NZ',
  SGD: 'S',
  TWD: 'NT',
  USD: 'US',
  FJD: 'FJ',
}

let regions: Array<App.RegionSummary> = []
// Potentially this module gets loaded before the config is initialised,
// so we need to be able to recompute this mapping if the config changes.
let formattingLocaleForRegionCode: { [key: string]: string } = {}

export function getFormattingLocaleForRegionCode(regionCode: string) {
  if (config.regions !== regions) {
    regions = config.regions
    formattingLocaleForRegionCode = arrayToObject(config.regions, region => region.code, region => region.currencyFormattingLocale)
  }
  return formattingLocaleForRegionCode[regionCode]
}

/**
 * Non-English UI locale for symbol placement; otherwise the region's formatting locale.
 * Arabic uses Western digits (`u-nu-latn`).
 *
 * `activeLocale` overrides `getCurrentIntl().locale`, keeping formatting request-scoped.
 */
function selectCurrencyFormattingLocale(regionCode: string, activeLocale?: string): string {
  const resolvedLocale = resolveActiveAppLocale(activeLocale ?? getCurrentIntl().locale)
  const currencyFormattingLocale = resolvedLocale && !isEnglishLocale(resolvedLocale)
    ? resolvedLocale
    : getFormattingLocaleForRegionCode(regionCode)

  return getLocaleWithWesternDigits(currencyFormattingLocale ?? DEFAULT_LOCALE)
}

/** Currency-specific tweaks on top of Intl output */
const formatTweak = switchFunc<(str: string) => string>({
  // Full-width yen looks poor in our font
  JPY: str => str.replace('￥', '¥'),
  // Force shekel on the left; strip bidi marks
  ILS: str => {
    if (str.endsWith('₪')) {
      return '₪ ' + str.replace(/[\u200e\u200f\s₪]/g, '')
    }
    return str
  },
}, str => str)

export function formatDynamicAmount(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  if (amount % 1) {
    return formatPreciseAmount(amount, currencyCode, regionCode, numberOptions, activeLocale)
  } else {
    return formatRoundedAmount(amount, currencyCode, regionCode, numberOptions, activeLocale)
  }
}

function envSupportsNarrowSymbol() {
  try {
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'narrowSymbol',
    })
    return true
  } catch {
    return false
  }
}

const currencyDisplay = envSupportsNarrowSymbol() ? 'narrowSymbol' : 'symbol'

/** Swap the currency part and drop Intl bidi-only literals (RLM/LRM). */
function joinCurrencyParts(
  parts: Array<Intl.NumberFormatPart>,
  currencySymbol: string,
): string {
  return parts
    .filter(part => !(part.type === 'literal' && /^[\u200e\u200f]+$/.test(part.value)))
    .map(part => part.type === 'currency' ? currencySymbol : part.value)
    .join('')
}

export function formatPreciseAmount(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  const formatter = new Intl.NumberFormat(selectCurrencyFormattingLocale(regionCode, activeLocale), {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay,
    ...numberOptions,
  })

  // Plain API: `$1,435` / Arabic `1,435 $` — never Intl's `AU$`. Brand prefixes are *Dollar APIs.
  if (currencyCode in dollarPrefixes) {
    return formatTweak(currencyCode)(joinCurrencyParts(formatter.formatToParts(amount), '$'))
  }

  return formatTweak(currencyCode)(formatter.format(amount).replace(/[\u200e\u200f]/g, ''))
}

export function formatRoundedAmount(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  return formatPreciseAmount(amount, currencyCode, regionCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...numberOptions,
  }, activeLocale)
}

export function addDollarType(formattedAmount: string, currencyCode: string): string {
  if (!config.multiCurrency) {
    return formattedAmount
  }

  if (formattedAmount.match(/^\$/)) {
    return ''.concat(dollarPrefixes[currencyCode] || '').concat(formattedAmount)
  }

  if (formattedAmount.match(/^\-\$/)) {
    return '-'.concat(dollarPrefixes[currencyCode] || '').concat(formattedAmount.slice(1))
  }

  if (formattedAmount.match(/^\+\$/)) {
    return '+'.concat(dollarPrefixes[currencyCode] || '').concat(formattedAmount.slice(1))
  }

  return formattedAmount
}

/** Brand prefixes with locale placement: `A$450` (en) / `450 A$` (ar). Prefer over addDollarType. */
function formatPreciseAmountWithBrandDollar(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  const prefix = config.multiCurrency ? dollarPrefixes[currencyCode] : undefined
  if (!prefix) {
    return formatPreciseAmount(amount, currencyCode, regionCode, numberOptions, activeLocale)
  }

  const formatter = new Intl.NumberFormat(selectCurrencyFormattingLocale(regionCode, activeLocale), {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay,
    ...numberOptions,
  })

  return formatTweak(currencyCode)(joinCurrencyParts(formatter.formatToParts(amount), `${prefix}$`))
}

/** Split brand symbol for UIs that render pre/post currency separately (e.g. Arena). */
export function getCurrencySymbolAffixes(
  currencyCode: string,
  regionCode: string,
  activeLocale?: string,
): { preSymbol: string; postSymbol: string } {
  // Match formatTweak: always put ₪ before the amount (Intl may place it after in Arabic).
  if (currencyCode === 'ILS') {
    return { preSymbol: '₪ ', postSymbol: '' }
  }

  const prefix = config.multiCurrency ? dollarPrefixes[currencyCode] : undefined
  const formatter = new Intl.NumberFormat(selectCurrencyFormattingLocale(regionCode, activeLocale), {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay,
  })

  const parts = formatter.formatToParts(0)
  const currencyIndex = parts.findIndex(part => part.type === 'currency')
  const integerIndex = parts.findIndex(part => part.type === 'integer')
  const symbol = formatTweak(currencyCode)(prefix ? `${prefix}$` : (parts[currencyIndex]?.value ?? ''))

  const symbolBeforeNumber = currencyIndex >= 0 && (integerIndex < 0 || currencyIndex < integerIndex)
  return {
    preSymbol: symbolBeforeNumber ? symbol : '',
    postSymbol: symbolBeforeNumber ? '' : symbol,
  }
}

export function formatDynamicAmountWithDollarType(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  if (amount % 1) {
    return formatPreciseAmountWithDollarType(amount, currencyCode, regionCode, numberOptions, activeLocale)
  }
  return formatRoundedAmountWithDollarType(amount, currencyCode, regionCode, numberOptions, activeLocale)
}

export function formatPreciseAmountWithDollarType(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  return formatPreciseAmountWithBrandDollar(amount, currencyCode, regionCode, numberOptions, activeLocale)
}

export function formatRoundedAmountWithDollarType(
  amount: number,
  currencyCode: string,
  regionCode: string,
  numberOptions?: Intl.NumberFormatOptions,
  activeLocale?: string,
): string {
  return formatPreciseAmountWithBrandDollar(amount, currencyCode, regionCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...numberOptions,
  }, activeLocale)
}

const oneAtStart = /^1\s*/
const oneAtEnd = /\s*1$/

export function blankAmount(currencyCode: string, regionCode: string, activeLocale?: string): string {
  return formatRoundedAmount(1, currencyCode, regionCode, undefined, activeLocale).replace(oneAtStart, '— ').replace(oneAtEnd, ' —')
}

export function blankAmountWithDollarType(currencyCode: string, regionCode: string, activeLocale?: string): string {
  return formatRoundedAmountWithDollarType(1, currencyCode, regionCode, undefined, activeLocale).replace(oneAtStart, '— ').replace(oneAtEnd, ' —')
}

/**
 * This is a custom non-Intl format option.
 */
export type CurrencyFormat = 'dynamicDollar' | 'dynamic' | 'preciseDollar' | 'precise' | 'blank' | 'blankDollar' | 'rounded' | 'roundedDollar'
export interface CurrencyFormatOptions extends Pick<Intl.NumberFormatOptions, 'notation' | 'maximumFractionDigits' | 'minimumFractionDigits' | 'signDisplay' | 'style'> {
  /**
   * @default dynamicDollar
   */
  format?: CurrencyFormat
  /** Overrides `getCurrentIntl().locale` for symbol placement/digits, keeping formatting request-scoped. */
  locale?: string
}

export function formatCurrency(
  value: number,
  currencyCode: string,
  regionCode: string,
  options: CurrencyFormatOptions = {},
): string {
  const { format = 'dynamicDollar', locale: activeLocale, ...numberOptions } = options
  const defaultedOptions = {
    ...numberOptions,
    signDisplay: numberOptions.signDisplay ?? 'auto',
  }

  // Show blank amount if `value` can't be parsed as a number (e.g. null or undefined)
  if (isNaN(parseFloat(value as any))) {
    return format.includes('Dollar') ?
        blankAmountWithDollarType(currencyCode, regionCode, activeLocale) :
        blankAmount(currencyCode, regionCode, activeLocale)
  }

  switch (format) {
    case 'blank':
      return blankAmount(currencyCode, regionCode, activeLocale)
    case 'blankDollar':
      return blankAmountWithDollarType(currencyCode, regionCode, activeLocale)
    case 'dynamic':
      return formatDynamicAmount(value, currencyCode, regionCode, defaultedOptions, activeLocale)
    case 'dynamicDollar':
      return formatDynamicAmountWithDollarType(value, currencyCode, regionCode, defaultedOptions, activeLocale)
    case 'preciseDollar':
      return formatPreciseAmountWithDollarType(value, currencyCode, regionCode, defaultedOptions, activeLocale)
    case 'precise':
      return formatPreciseAmount(value, currencyCode, regionCode, defaultedOptions, activeLocale)
    case 'rounded':
      return formatRoundedAmount(value, currencyCode, regionCode, defaultedOptions, activeLocale)
    case 'roundedDollar':
      return formatRoundedAmountWithDollarType(value, currencyCode, regionCode, defaultedOptions, activeLocale)
  }
}
